import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  resetFunnelOrder,
  setBuilderAdditionalSettings,
} from '../../../../../redux/slices/oldBuilder';
import BuilderSamplePercentage from '../../../../../components/Builder/BuilderFields/BuilderSamplePercentage';
import BuilderFunnelOrder from './BuilderFunnelOrder';
import BuilderFunnelMode from './BuilderFunnelMode';
import Partition from '../../../../../components/Builder/Partition';
import {
  FunnelQuerySettingKey,
  PermissionKey,
  QueryType,
} from '../../../../../utils/apollo/graphqlSchema';
import AdditionalSettings from '../../../../../components/Builder/AdditionalSettings';
import {
  activeSchemaIdSelector,
  additionalSettingsSelector,
  builderSelectedOptionsSelector,
  defaultAdditionalSettingsSelector,
} from '../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../utils/types';
import { SUBJECT_EVENT_KEY } from '../../../../../components/Chart/constants/utils';
import {
  BuilderHeader,
  BuilderHeaderGroupby,
  BuilderHeaderSubject,
} from '../../../../../components/Builder/BuilderHeader';
import ThinDivider from '../../../../../components/ThinDivider';
import useShowLumosButton from '../../../../../components/LumosButton/useShowLumosButton';
import LumosButton from '../../../../../components/LumosButton';

const queryType = QueryType.Funnel;

const FunnelBuilderHeader = () => {
  const dispatch = useAppDispatch();

  const defaultAdditionalSettings = useAppSelector(
    defaultAdditionalSettingsSelector,
  );
  const additionalSettings = useAppSelector(additionalSettingsSelector);
  const selectedOptions = useAppSelector(builderSelectedOptionsSelector);

  assertCondition(selectedOptions?.type === queryType);

  const selectedSubjectKey = selectedOptions.subject;

  const isEventSubject = selectedSubjectKey === SUBJECT_EVENT_KEY;

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const shouldShowLumosButton = useShowLumosButton(
    activeSchemaId,
    PermissionKey.AnalysisCreate,
  );

  const additionalSettingsOptions = useMemo(() => {
    if (!isEventSubject) {
      return defaultAdditionalSettings;
    }

    return defaultAdditionalSettings.filter(({ key }) => {
      if (isEventSubject && key === FunnelQuerySettingKey.GroupBy) {
        return false;
      }

      return true;
    });
  }, [defaultAdditionalSettings, isEventSubject]);

  const checkSelectedSettings = (key: FunnelQuerySettingKey) =>
    additionalSettings.includes(key);

  const handleChangeSubject = (selectedSubject: string) => {
    dispatch(resetFunnelOrder());

    // remove GroupBy additional option in case when subject=EVENT
    if (
      additionalSettings.includes(FunnelQuerySettingKey.GroupBy) &&
      selectedSubject === SUBJECT_EVENT_KEY
    ) {
      dispatch(
        setBuilderAdditionalSettings(
          additionalSettings.filter(
            (as) => as !== FunnelQuerySettingKey.GroupBy,
          ),
        ),
      );
    }
  };

  return (
    <BuilderHeader queryType={queryType}>
      <ThinDivider layout="vertical" size="short" />
      <BuilderHeaderSubject
        queryType={queryType}
        onChange={handleChangeSubject}
      />

      <ThinDivider layout="vertical" size="short" />
      <BuilderFunnelOrder />

      <ThinDivider layout="vertical" size="short" />
      <BuilderFunnelMode />

      {checkSelectedSettings(FunnelQuerySettingKey.Partition) && (
        <>
          <ThinDivider layout="vertical" size="short" />
          <Partition queryType={queryType} />
        </>
      )}

      {!isEventSubject &&
        checkSelectedSettings(FunnelQuerySettingKey.GroupBy) && (
          <>
            <ThinDivider layout="vertical" size="short" />
            <BuilderHeaderGroupby queryType={queryType} />
          </>
        )}

      {checkSelectedSettings(FunnelQuerySettingKey.Sampling) && (
        <>
          <ThinDivider layout="vertical" size="short" />
          <BuilderSamplePercentage />
        </>
      )}

      <ThinDivider layout="vertical" size="short" />
      <AdditionalSettings
        options={additionalSettingsOptions}
        selectedKeys={additionalSettings}
      />
      {shouldShowLumosButton && <LumosButton />}
    </BuilderHeader>
  );
};

export default FunnelBuilderHeader;
